/*
 * SPDX-License-Identifier: MIT
 * Copyright (c) 2024 Tech Inverge Solutions Pvt Ltd
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, ArrowLeft, Search, RefreshCw, Eye, User, Mail, Phone, MapPin, DollarSign, Truck, Check, X, Loader } from 'lucide-react';
import { buildApiUrl } from '../config';

interface OrderItem {
  productId: {
    _id: string;
    title: string;
    price: number;
    category: string;
    image?: string;
    colorVariants?: any[];
  } | null;
  quantity: number;
  priceAtPurchase: number;
  selectedColor?: string;
  _id: string;
}

interface Order {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  } | null;
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  razorpaySignature?: string;
  paymentMethod?: 'Prepaid' | 'COD';
  items: OrderItem[];
  amount: number;
  currency: string;
  status: 'created' | 'placed' | 'paid' | 'failed' | 'cod_pending';
  receipt: string;
  shippingAddress?: {
    firstName: string;
    lastName: string;
    phone: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  shiprocketOrderId?: string;
  shipmentId?: string;
  awbCode?: string;
  trackingStatus?: string;
  createdAt: string;
  updatedAt: string;
}

const OrderManagement: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'paid' | 'created' | 'failed'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 20;

  // Modal State
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState<'created' | 'paid' | 'failed'>('created');
  const [trackingStatusToUpdate, setTrackingStatusToUpdate] = useState('');
  const [awbCodeToUpdate, setAwbCodeToUpdate] = useState('');
  const [shipmentIdToUpdate, setShipmentIdToUpdate] = useState('');

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      throw new Error('No token found');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    } as HeadersInit;

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
      throw new Error('Session expired');
    }

    return response;
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage, activeFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });

      if (activeFilter !== 'all') {
        params.append('status', activeFilter);
      }

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      const response = await authenticatedFetch(
        buildApiUrl(`/api/admin/orders?${params}`)
      );

      const data = await response.json();
      if (data.success) {
        setOrders(data.orders || []);
        setTotal(data.pagination.total || 0);
        setTotalPages(data.pagination.totalPages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchOrders();
  };

  const openOrderDetails = async (orderId: string) => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(buildApiUrl(`/api/admin/orders/${orderId}`));
      const data = await response.json();
      
      if (data.success && data.order) {
        setSelectedOrder(data.order);
        setStatusToUpdate(data.order.status);
        setTrackingStatusToUpdate(data.order.trackingStatus || '');
        setAwbCodeToUpdate(data.order.awbCode || '');
        setShipmentIdToUpdate(data.order.shipmentId || '');
        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      alert('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrder) return;

    setIsUpdatingStatus(true);
    try {
      const response = await authenticatedFetch(
        buildApiUrl(`/api/admin/orders/${selectedOrder._id}/status`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: statusToUpdate,
            trackingStatus: trackingStatusToUpdate || null,
            awbCode: awbCodeToUpdate || null,
            shipmentId: shipmentIdToUpdate || null,
          }),
        }
      );

      const data = await response.json();
      if (response.ok && data.success) {
        alert('Order updated successfully!');
        // Update selected order details in view
        setSelectedOrder(data.order);
        // Refresh local orders list
        fetchOrders();
      } else {
        alert(data.message || 'Failed to update order');
      }
    } catch (error) {
      console.error('Update status error:', error);
      alert('An error occurred while updating order');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'placed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'created':
      case 'cod_pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 pb-5 border-b border-gray-200">
          <div className="flex items-center space-x-4 mb-4 md:mb-0">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 rounded-lg shadow-sm transition-colors"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center space-x-2">
                <ShoppingBag className="w-6 h-6 text-blue-600" />
                <h1 className="text-2xl font-bold text-gray-900">Order Management</h1>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                View customer orders, search, filter, and track Shiprocket sync status
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm font-semibold text-gray-700 bg-white border border-gray-200 px-3 py-1.5 rounded-lg shadow-sm">
              Total Orders: {total}
            </span>
            <button
              onClick={fetchOrders}
              className="p-2 bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 rounded-lg shadow-sm transition-colors"
              title="Refresh List"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters and search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            
            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-2">
              {(['all', 'paid', 'created', 'failed'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => {
                    setActiveFilter(filter);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-lg capitalize border transition-all ${
                    activeFilter === filter
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>

            {/* Search form */}
            <form onSubmit={handleSearchSubmit} className="flex-1 lg:max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Order ID, receipt, name, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-24 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button
                  type="submit"
                  className="absolute right-1.5 top-1.5 px-3 py-1 bg-gray-800 text-white rounded-md hover:bg-gray-900 transition-colors text-xs font-semibold"
                >
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading && orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader className="w-10 h-10 text-blue-600 animate-spin mb-4" />
              <p className="text-gray-500 text-sm">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">No Orders Found</h3>
              <p className="text-gray-500 mt-1 max-w-sm mx-auto">
                No orders match your current filters or search terms. Try clearing search or status filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider">
                    <th className="px-6 py-4">Date & Time</th>
                    <th className="px-6 py-4">Order ID / Receipt</th>
                    <th className="px-6 py-4">Customer</th>
                    <th className="px-6 py-4 text-right">Amount</th>
                    <th className="px-6 py-4">Shiprocket Status</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
                  {orders.map((order) => {
                    const orderDate = new Date(order.createdAt).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });

                    return (
                      <tr key={order._id} className="hover:bg-gray-50/70 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-xs">
                          {orderDate}
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-semibold text-gray-900">{order.razorpayOrderId || order._id}</div>
                          <div className="text-xs text-gray-400">
                            Receipt: {order.receipt}
                            {order.paymentMethod && (
                              <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                order.paymentMethod === 'COD' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                              }`}>
                                {order.paymentMethod}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-semibold text-gray-900">
                            {order.shippingAddress 
                              ? `${order.shippingAddress.firstName} ${order.shippingAddress.lastName}`
                              : order.userId 
                                ? `${order.userId.firstName} ${order.userId.lastName}`
                                : 'Guest'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {order.userId?.email || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-gray-900 whitespace-nowrap">
                          ₹{order.amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {order.shiprocketOrderId ? (
                            <div className="flex flex-col">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 max-w-fit">
                                Sync Success
                              </span>
                              <span className="text-xs text-gray-400 mt-1">ID: {order.shiprocketOrderId}</span>
                              {order.trackingStatus && (
                                <span className="text-xs text-gray-500 font-semibold mt-0.5">
                                  Status: {order.trackingStatus}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200">
                              No Shiprocket ID
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border capitalize ${getStatusBadgeClass(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => openOrderDetails(order._id)}
                            className="inline-flex items-center justify-center p-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-600 rounded-lg shadow-sm transition-all"
                            title="View Order Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Footer */}
          {totalPages > 1 && (
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Showing Page {currentPage} of {totalPages} (Total {total} items)
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Order Details Modal Overlay */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-4xl overflow-hidden max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-150">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between bg-gray-50 px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Order Details: {selectedOrder.razorpayOrderId || selectedOrder._id}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Receipt: {selectedOrder.receipt} | ID: {selectedOrder._id}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedOrder(null);
                }}
                className="p-1.5 bg-white hover:bg-gray-100 border border-gray-200 text-gray-500 hover:text-gray-700 rounded-lg shadow-sm transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-grow overflow-y-auto p-6 space-y-6">
              
              {/* Top summary layout */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* Customer Information */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                  <h4 className="font-bold text-gray-900 border-b border-gray-200 pb-2 text-xs uppercase tracking-wide flex items-center">
                    <User className="w-4 h-4 text-blue-600 mr-2" />
                    Customer Info
                  </h4>
                  <div className="text-sm space-y-2">
                    <div>
                      <span className="text-xs text-gray-500 block">Name</span>
                      <span className="font-semibold text-gray-900">
                        {selectedOrder.shippingAddress 
                          ? `${selectedOrder.shippingAddress.firstName} ${selectedOrder.shippingAddress.lastName}`
                          : selectedOrder.userId 
                            ? `${selectedOrder.userId.firstName} ${selectedOrder.userId.lastName}`
                            : 'Guest'}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Email Address</span>
                      <span className="font-semibold text-gray-900 flex items-center text-xs">
                        <Mail className="w-3 h-3 text-gray-400 mr-1.5" />
                        {selectedOrder.userId?.email || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Phone Number</span>
                      <span className="font-semibold text-gray-900 flex items-center text-xs">
                        <Phone className="w-3 h-3 text-gray-400 mr-1.5" />
                        {selectedOrder.shippingAddress?.phone || selectedOrder.userId?.phone || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Delivery Shipping Address */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                  <h4 className="font-bold text-gray-900 border-b border-gray-200 pb-2 text-xs uppercase tracking-wide flex items-center">
                    <MapPin className="w-4 h-4 text-green-600 mr-2" />
                    Shipping Address
                  </h4>
                  {selectedOrder.shippingAddress ? (
                    <div className="text-sm space-y-1.5">
                      <span className="font-semibold text-gray-900 block">
                        {selectedOrder.shippingAddress.firstName} {selectedOrder.shippingAddress.lastName}
                      </span>
                      <span className="text-gray-600 text-xs block leading-relaxed">
                        {selectedOrder.shippingAddress.street}<br />
                        {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state}<br />
                        PIN Code: <span className="font-bold text-gray-900">{selectedOrder.shippingAddress.zip}</span><br />
                        {selectedOrder.shippingAddress.country}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-500 italic">No shipping address provided</span>
                  )}
                </div>

                {/* Order Payment Status */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                  <h4 className="font-bold text-gray-900 border-b border-gray-200 pb-2 text-xs uppercase tracking-wide flex items-center">
                    <DollarSign className="w-4 h-4 text-yellow-600 mr-2" />
                    Payment Details
                  </h4>
                  <div className="text-sm space-y-2">
                    <div>
                      <span className="text-xs text-gray-500 block">Total Amount Paid</span>
                      <span className="text-lg font-bold text-gray-900">₹{selectedOrder.amount.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Razorpay Payment ID</span>
                      <span className="font-semibold text-gray-800 text-xs break-all">
                        {selectedOrder.razorpayPaymentId || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Razorpay Signature</span>
                      <span className="font-semibold text-gray-800 text-xs break-all truncate block" title={selectedOrder.razorpaySignature}>
                        {selectedOrder.razorpaySignature || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

              </div>

              {/* Shiprocket integration status card */}
              <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-bold text-gray-900 border-b border-blue-200 pb-2 text-xs uppercase tracking-wide flex items-center">
                  <Truck className="w-4 h-4 text-blue-600 mr-2" />
                  Shiprocket Shipping Sync
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3 text-sm">
                  <div>
                    <span className="text-xs text-gray-500 block">Shiprocket Order ID</span>
                    <span className="font-semibold text-gray-900 text-xs">
                      {selectedOrder.shiprocketOrderId || 'Not synced'}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block">Shipment ID</span>
                    <span className="font-semibold text-gray-900 text-xs">
                      {selectedOrder.shipmentId || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block">AWB Tracking Code</span>
                    <span className="font-semibold text-gray-900 text-xs font-mono">
                      {selectedOrder.awbCode || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block">Courier Status</span>
                    <span className="inline-flex items-center px-2 py-0.5 mt-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200 uppercase">
                      {selectedOrder.trackingStatus || 'Unassigned'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order items listing */}
              <div>
                <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wide mb-3 flex items-center">
                  <ShoppingBag className="w-4 h-4 text-gray-500 mr-2" />
                  Ordered Items
                </h4>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50 text-gray-500 text-xs font-semibold border-b border-gray-200 uppercase tracking-wider">
                        <th className="px-4 py-3">Product Name</th>
                        <th className="px-4 py-3 text-right">Price</th>
                        <th className="px-4 py-3 text-center">Qty</th>
                        <th className="px-4 py-3 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 text-sm">
                      {selectedOrder.items.map((item) => {
                        const product = item.productId;
                        const variantImage = item.selectedColor && product?.colorVariants?.find((v: any) => v.name === item.selectedColor)?.image;
                        const itemImg = variantImage || product?.image;

                        return (
                          <tr key={item._id} className="hover:bg-gray-50/30">
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-3">
                                {itemImg && (
                                  <img 
                                    src={itemImg} 
                                    alt={product?.title || 'Product'} 
                                    className="w-10 h-10 object-cover rounded-md border border-gray-100 flex-shrink-0"
                                  />
                                )}
                                <div>
                                  <div className="font-semibold text-gray-900">{product ? product.title : 'Deleted Product'}</div>
                                  <div className="flex items-center space-x-2 mt-0.5">
                                    <span className="text-xs text-gray-500 uppercase tracking-wider">{product?.category || 'N/A'}</span>
                                    {item.selectedColor && (
                                      <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
                                        Color: {item.selectedColor}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right whitespace-nowrap text-gray-500">
                              ₹{item.priceAtPurchase.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-center font-semibold text-gray-900">
                              {item.quantity}
                            </td>
                            <td className="px-4 py-3 text-right whitespace-nowrap font-bold text-gray-900">
                              ₹{(item.priceAtPurchase * item.quantity).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-gray-50 font-bold text-gray-900 border-t border-gray-200 text-sm">
                        <td colSpan={3} className="px-4 py-3 text-right">Total:</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap text-base text-blue-600">
                          ₹{selectedOrder.amount.toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

            </div>

            {/* Modal Footer / Update order state */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <form onSubmit={handleUpdateStatusSubmit} className="flex-1 max-w-2xl flex flex-wrap items-center gap-3">
                <div className="flex items-center space-x-2">
                  <label htmlFor="status" className="text-xs font-bold text-gray-700 uppercase">Payment:</label>
                  <select
                    id="status"
                    value={statusToUpdate}
                    onChange={(e) => setStatusToUpdate(e.target.value as any)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white text-xs font-bold"
                  >
                    <option value="created">Created</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <label htmlFor="trackingStatus" className="text-xs font-bold text-gray-700 uppercase">Shipping:</label>
                  <input
                    type="text"
                    id="trackingStatus"
                    placeholder="AWB status e.g. Shipped"
                    value={trackingStatusToUpdate}
                    onChange={(e) => setTrackingStatusToUpdate(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-xs"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <label htmlFor="awbCode" className="text-xs font-bold text-gray-700 uppercase">AWB:</label>
                  <input
                    type="text"
                    id="awbCode"
                    placeholder="AWB number"
                    value={awbCodeToUpdate}
                    onChange={(e) => setAwbCodeToUpdate(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-xs font-mono"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isUpdatingStatus}
                  className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg shadow-sm text-xs font-bold transition-colors flex items-center"
                >
                  {isUpdatingStatus ? (
                    <>
                      <Loader className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5 mr-1.5" />
                      Save Changes
                    </>
                  )}
                </button>
              </form>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedOrder(null);
                  }}
                  className="px-4 py-1.5 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default OrderManagement;
